
import React from "react";
import { CampaignData } from "./CampaignBuilder";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Clock } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

interface CampaignSchedulerProps {
  campaignData: CampaignData;
  updateCampaignData: (data: Partial<CampaignData>) => void;
}

// Mock data for timezones - in a real app this would come from a library or API
const timezones = [
  { value: "UTC", label: "UTC (Coordinated Universal Time)" },
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "Europe/London", label: "Greenwich Mean Time (GMT)" },
  { value: "Europe/Paris", label: "Central European Time (CET)" },
  { value: "Asia/Tokyo", label: "Japan Standard Time (JST)" },
];

export const CampaignScheduler: React.FC<CampaignSchedulerProps> = ({
  campaignData,
  updateCampaignData,
}) => {
  const [scheduleType, setScheduleType] = React.useState<"now" | "later">(
    campaignData.sendDate ? "later" : "now"
  );
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(
    campaignData.sendDate || undefined
  );
  const [sendTime, setSendTime] = React.useState<string>(
    campaignData.sendDate ? format(campaignData.sendDate, "HH:mm") : "12:00"
  );

  // Update campaign data when schedule options change
  React.useEffect(() => {
    if (scheduleType === "now") {
      updateCampaignData({ sendDate: undefined });
    } else if (scheduleType === "later" && selectedDate) {
      // Combine date and time for the final sendDate
      const [hours, minutes] = sendTime.split(":").map(Number);
      const sendDate = new Date(selectedDate);
      sendDate.setHours(hours, minutes, 0, 0);
      updateCampaignData({ sendDate });
    }
  }, [scheduleType, selectedDate, sendTime, updateCampaignData]);

  return (
    <div className="space-y-6">
      <Card className="border border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Delivery Timing</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={scheduleType}
            onValueChange={(value) => setScheduleType(value as "now" | "later")}
            className="space-y-4"
          >
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="now" id="now" />
              <div className="space-y-1">
                <Label htmlFor="now" className="font-medium">Send immediately</Label>
                <p className="text-sm text-slate-500">Your campaign will be sent right after you confirm</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <RadioGroupItem value="later" id="later" />
              <div className="space-y-1">
                <Label htmlFor="later" className="font-medium">Schedule for later</Label>
                <p className="text-sm text-slate-500">Set a specific date and time to send your campaign</p>
              </div>
            </div>
          </RadioGroup>

          {scheduleType === "later" && (
            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="date">Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id="date"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal border-slate-200",
                          !selectedDate && "text-slate-500"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, "PPP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        initialFocus
                        disabled={(date) => date < new Date()}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="time">Time</Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="time"
                      type="time"
                      className="pl-9"
                      value={sendTime}
                      onChange={(e) => setSendTime(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="timezone">Timezone</Label>
                <Select
                  value={campaignData.timezone}
                  onValueChange={(value) => updateCampaignData({ timezone: value })}
                >
                  <SelectTrigger id="timezone" className="border-slate-200">
                    <SelectValue placeholder="Select a timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    {timezones.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Tracking Options</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="track-opens">Track email opens</Label>
              <p className="text-xs text-slate-500">
                Track when recipients open your email
              </p>
            </div>
            <Switch
              id="track-opens"
              checked={campaignData.trackOpens}
              onCheckedChange={(checked) => updateCampaignData({ trackOpens: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="track-clicks">Track link clicks</Label>
              <p className="text-xs text-slate-500">
                Track when recipients click links in your email
              </p>
            </div>
            <Switch
              id="track-clicks"
              checked={campaignData.trackClicks}
              onCheckedChange={(checked) => updateCampaignData({ trackClicks: checked })}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Advanced Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-1.5">
            <Label htmlFor="send-rate">Send rate (emails per hour)</Label>
            <Input
              id="send-rate"
              type="number"
              min="10"
              max="10000"
              placeholder="Unlimited"
              value={campaignData.sendRate || ""}
              onChange={(e) => {
                const value = e.target.value === "" ? undefined : Number(e.target.value);
                updateCampaignData({ sendRate: value });
              }}
              className="border-slate-200"
            />
            <p className="text-xs text-slate-500">
              Limit how many emails are sent per hour (leave empty for unlimited)
            </p>
          </div>
          
          <div className="space-y-1.5">
            <Label htmlFor="delivery-optimization">Delivery Optimization</Label>
            <Select
              disabled={true}
              defaultValue="standard"
            >
              <SelectTrigger id="delivery-optimization" className="border-slate-200">
                <SelectValue placeholder="Select optimization mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard delivery</SelectItem>
                <SelectItem value="optimized">Time-optimized delivery</SelectItem>
                <SelectItem value="engagement">Engagement-optimized delivery</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center mt-1">
              <p className="text-xs text-slate-500 flex items-center">
                Smart delivery optimization
                <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-800 border-amber-200">
                  Phase 4 Feature
                </Badge>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
